// A user's own copy of one of Sayer's panel forecasts (opened from a card on the new-question
// page). The server creates the row (POST /api/sayer-forecasts/copy); this module recognises one
// and builds the stand-in the client shows while that request is in flight. Pure (no React).
//
// A copy follows Sayer: when Sayer publishes a newer forecast for its question, the server points
// the copy at it as the list loads (server/sayerForecasts/follow.js) and it becomes current. The
// first thing only an owner can do (Update now, or a change to the schedule or depth) makes it
// theirs (hooks/useSayerCopyOwnership.js, POST /api/sayer-forecasts/copy/convert).

import { laneTier } from "../forecast/engines.js";

export const OBSERVER_ORIGIN = "observer";
export const SAYER_MANAGED_NOTE = "Sayer made this forecast and keeps it up to date.";

/** The `server_ctx.origin` of an observer copy, or null. Reads a Run or a list row. */
export function observerOrigin(f) {
  const o = f?.serverContext?.origin;
  return o && o.kind === OBSERVER_ORIGIN && typeof o.questionKey === "string" ? o : null;
}

/** Is this forecast a copy of one of Sayer's own forecasts, not yet made the user's own? */
export const isSayerManaged = (f) => observerOrigin(f) != null;

/** What the locked Updates rows say for a copy: it updates with Sayer, at Sayer's depth. */
export function sayerManagedUpdates(f) {
  const o = observerOrigin(f);
  if (!o) return null;
  const tier = o.tier || laneTier(o.lane);
  return { cadence: "With Sayer", depth: tier === "deep" ? "Deep" : "Quick" };
}

/** How every copy's client id for `questionKey` starts; it outlives the copy becoming the user's. */
export function observerClientPrefix(questionKey) {
  const slug = String(questionKey || "q")
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .slice(0, 60);
  return `obs-${slug}-`;
}

/**
 * The user's existing copy of `questionKey` among `runs` (Run objects), or null: one still
 * Sayer's, or one they have made their own (found by its client id).
 */
export function findObserverCopy(runs, questionKey) {
  const prefix = observerClientPrefix(questionKey);
  for (const r of Array.isArray(runs) ? runs : []) {
    if (!r || r.parentRunId) continue;
    if (observerOrigin(r)?.questionKey === questionKey) return r;
    if (typeof r.runId === "string" && r.runId.startsWith(prefix)) return r;
  }
  return null;
}

/**
 * The question keys among `items` the user already has a copy of (Sayer's or made their own),
 * from `runs` (Run objects) in one pass, so the landing cards can skip them.
 * @returns {string[]}
 */
export function ownedQuestionKeys(runs, items) {
  const byOrigin = new Set();
  const byPrefix = new Set();
  for (const r of Array.isArray(runs) ? runs : []) {
    if (!r || r.parentRunId) continue;
    const key = observerOrigin(r)?.questionKey;
    if (key) byOrigin.add(key);
    // A copy's id is its question's prefix plus a dash-free suffix.
    if (typeof r.runId === "string" && r.runId.startsWith("obs-")) {
      byPrefix.add(r.runId.slice(0, r.runId.lastIndexOf("-") + 1));
    }
  }
  return (Array.isArray(items) ? items : [])
    .map((it) => it?.questionKey)
    .filter((k) => k && (byOrigin.has(k) || byPrefix.has(observerClientPrefix(k))));
}

/** A fresh client id for a copy: readable, unique, and inside the server's id alphabet. */
export function observerClientId(questionKey, rand = Math.random) {
  const suffix = Math.floor(rand() * 0x100000000)
    .toString(16)
    .padStart(8, "0");
  return `${observerClientPrefix(questionKey)}${suffix}`;
}

/** "20261009_004130" → "2026-10-09T00:41:30.000Z", or null. */
export function fireTsToIso(fireTs) {
  const m = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/.exec(String(fireTs || ""));
  if (!m) return null;
  const [Y, Mo, D, h, mi, s] = m.slice(1).map(Number);
  return new Date(Date.UTC(Y, Mo - 1, D, h, mi, s)).toISOString();
}

/** The resolution date the card's question closes on, "YYYY-MM-DD" (UTC), or "". */
export function closeDateOf(item) {
  const s = String(item?.closeDate || "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

/**
 * The wire row (rowToWire's shape) shown the moment a card is clicked: the question, Sayer's
 * answer and the report pointer from the snapshot. The server's row replaces it once created.
 * @param {object} item a snapshot item
 * @param {{clientId: string, now?: Date}} opts
 */
export function observerStubRow(item, { clientId, now = new Date() }) {
  const fv = item.forecastValue ?? null;
  const mass = fv?.representation?.mass;
  const binary = item.kind === "binary";
  const pYes = binary && Array.isArray(mass) && mass.length === 2 ? mass[1] : null;
  const firedAt = fireTsToIso(item.fireTs) || item.firedAt || now.toISOString();
  const openedAt = now.toISOString();
  return {
    id: clientId,
    forecastId: null,
    question: item.title,
    contractQuestion: item.title,
    createdAt: firedAt,
    receivedAt: openedAt,
    contract: {
      title: item.title,
      question: item.title,
      resolution_date: closeDateOf(item),
      resolution_criteria: "",
      probability: pYes,
      forecast_value: fv,
      ...(binary ? { outcome_labels: ["no", "yes"] } : {}),
    },
    events: [],
    serverPrediction: pYes,
    serverExplanation: "",
    serverContext: {
      metadata: { orchestrator: item.lane },
      origin: {
        kind: OBSERVER_ORIGIN,
        questionKey: item.questionKey,
        rowKey: item.rowKey,
        lane: item.lane,
        tier: item.tier ?? null,
        fireTs: item.fireTs,
        openedAt,
      },
    },
    status: "completed",
    traceId: null,
    schedule: null,
    notify: {},
    history: [],
    reportPointers: [
      {
        kind: "forecast",
        source: "panel",
        forecaster: item.lane,
        row_key: item.rowKey,
        fire_ts: item.fireTs,
        at: openedAt,
        tag: null,
      },
    ],
  };
}
